import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { ShieldCheck, Eye, CalendarCheck, Lock, LogIn, Languages, Maximize2 } from 'lucide-react';
import { WavePattern } from './WavePattern';
import { industries, KANRIVA_HERO_SHOT } from './constants';
import { ScreenshotLightbox, demoNoteFor } from './ScreenshotLightbox';
import { useLang } from '../../contexts/LanguageContext';
import { WALKTHROUGH_MAILTO } from '../../i18n/trust';

// Company-focused hero. All copy comes from i18n; only icons + images
// live in code. Industry chips zip the icon array from constants with
// translated names from the i18n `industries.names` array by index.
export const HeroSection = () => {
  const { t, lang } = useLang('hero');
  const { t: ind } = useLang('industries');
  const [zoom, setZoom] = useState(false);
  const demoNote = demoNoteFor(lang);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden z-10">
      <WavePattern className="inset-0 w-full h-full" opacity={0.18} />
      <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-[#00CED1]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-2 mb-8">
              <span className="h-6 w-1 bg-[#00CED1] rounded-full" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00CED1]">
                {t.eyebrow}
              </span>
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-6 text-white"
              style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
            >
              {t.titleA}
              <br />
              {t.titleB}{' '}
              <span className="text-[#00CED1]">{t.titleC}</span>.
            </h1>

            <p className="text-lg font-semibold text-[#00CED1] mb-3 max-w-xl" data-testid="hero-lede">
              {t.lede}
            </p>

            <p className="text-lg text-white/65 leading-relaxed mb-6 max-w-xl">
              {t.description}
            </p>

            <div
              className="flex flex-wrap items-center gap-2 mb-10 max-w-xl"
              data-testid="hero-trust-strip"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00CED1]/10 border border-[#00CED1]/25 text-[#00CED1] text-xs font-semibold">
                <ShieldCheck className="h-3.5 w-3.5" />
                {t.trustBadge}
              </div>
              <span className="text-xs text-white/45">{t.trustSub}</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch gap-3">
              <Button
                size="lg"
                className="h-auto min-h-14 px-8 py-3 text-base whitespace-nowrap bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold shadow-[0_8px_30px_-12px_rgba(0,206,209,0.65)]"
                asChild
                data-testid="hero-cta-login"
              >
                <Link to="/login">
                  <LogIn className="mr-2 h-5 w-5" /> {t.ctaLogin || 'Log in'}
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-auto min-h-14 max-w-[260px] px-5 py-3 text-sm whitespace-normal leading-snug border-[#00CED1]/40 text-[#00CED1] hover:bg-[#00CED1]/10"
                asChild
                data-testid="hero-cta-walkthrough"
              >
                <a href={WALKTHROUGH_MAILTO}>
                  <CalendarCheck className="mr-2 h-5 w-5 shrink-0" /> {t.ctaWalkthrough}
                </a>
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="h-auto min-h-14 px-5 py-3 text-sm whitespace-nowrap text-white/70 hover:text-white hover:bg-white/5 border border-white/10"
                asChild
                data-testid="hero-cta-peek"
              >
                <Link to="/demo">
                  <Eye className="mr-2 h-5 w-5 shrink-0" /> {t.ctaPeek}
                </Link>
              </Button>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/40">
              <Link to="/signup" className="text-white/60 hover:text-[#00CED1] underline underline-offset-4 transition-colors" data-testid="hero-cta-trial">
                {t.ctaTrialSmall}
              </Link>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-white/45" data-testid="hero-language-note">
              <Languages className="h-4 w-4 text-[#00CED1] shrink-0" />
              <span>{t.languageNote}</span>
              <a href="#language" className="text-[#00CED1] hover:underline font-semibold">{t.languageNoteLink}</a>
            </div>

            <div className="mt-8 flex flex-wrap items-start gap-2 max-w-xl" data-testid="hero-trust-line">
              <Lock className="h-4 w-4 text-[#00CED1] mt-0.5 shrink-0" />
              <p className="text-xs text-white/45 leading-relaxed">
                {t.trustLine}{' '}
                <Link to="/security" className="text-[#00CED1] hover:underline font-semibold" data-testid="hero-security-link">
                  {t.trustLink} →
                </Link>
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="text-xs text-white/30 mb-4 uppercase tracking-wider">{t.industriesLabel}</p>
              <div className="flex flex-wrap gap-3">
                {industries.map((iconRow, i) => {
                  const Icon = iconRow.icon;
                  const name = ind.names?.[i] || iconRow.name;
                  return (
                    <div key={name} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60">
                      <Icon className="h-4 w-4 text-[#00CED1]" />
                      <span className="text-sm">{name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <div className="relative group">
              <button
                type="button"
                onClick={() => setZoom(true)}
                className="relative block cursor-zoom-in rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00CED1]"
                aria-label={`Kanriva™ — KANRI Method, today's condition — ${demoNote}`}
                data-testid="hero-preview-zoom"
              >
                <img
                  src={KANRIVA_HERO_SHOT}
                  alt="Kanriva™ — KANRI Method, today's condition (demo data)"
                  className="w-full max-w-[640px] rounded-2xl shadow-2xl shadow-[#00CED1]/10 border border-white/10"
                  data-testid="hero-dashboard-preview"
                />
                <span className="pointer-events-none absolute right-3 top-3 h-8 w-8 rounded-md bg-black/60 border border-white/20 text-white/80 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                  <Maximize2 className="h-4 w-4" />
                </span>
              </button>
              <div className="absolute -inset-4 bg-[#00CED1]/5 rounded-3xl blur-2xl -z-10" />
            </div>
          </div>
        </div>
      </div>

      <ScreenshotLightbox
        shot={zoom ? KANRIVA_HERO_SHOT : null}
        caption="Kanriva™ — KANRI Method, today's condition"
        note={demoNote}
        onClose={() => setZoom(false)}
      />

    </section>
  );
};
